import { arrayOf, bool, func, oneOfType, shape, number, string } from "prop-types";
import { FORM_ID } from "constants/servicePayments";
import React, { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import Spinner from "pages/_components/Spinner";

import { selectors as servicePaymentsSelectors, actions as servicePaymentsActions } from "reducers/servicePayments";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as accountsSelectors } from "reducers/accounts";

import * as i18nUtils from "util/i18n";
import * as configUtils from "util/config";
import { isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";
import { V3_SCROLL_ROOT_ID } from "v3/constants";

import { Form, withFormik } from "formik";
import * as Yup from "yup";
import Table from "pages/_components/Table";
import classNames from "classnames";
import { Grid, Col as UiKitCol } from "@ui-kit/components/index.es";
import Image from "pages/_components/Image";
import I18n from "pages/_components/I18n";
import { compose } from "redux";
import { connect } from "react-redux";
import { buildPreparePaymentsRequest } from "util/servicePayments";
import Checkbox from "pages/_components/Checkbox";
import Pagination from "pages/_components/pagination/Pagination";
import ListItem from "./MultiplePaymentsListItem";
import ListHeader from "./MultiplePaymentsListHeader";
import PaymentAFIPList from "../_components/PaymentAFIPList";
import MultipleAFIPList from "./MultipleAFIPList";

const MultiplePaymentsList = ({
    isDesktop,
    isSmallDesktop,
    servicePaymentsSelected,
    accounts,
    account,
    submitForm,
    dispatch,
    flatPayments,
    paymentsInfo,
    isRetailEnvironment,
    setFieldValue,
    setErrors,
    setTouched,
    totalPages,
    pageNumber,
    fetchNextPage,
    defaultAccount,
}) => {
    const [allSelected, setAllSelected] = useState(false);
    const [disableButton, setDisableButton] = useState(true);
    const [payments, setPayments] = useState(flatPayments || []);
    const currencies = [{ id: 0, label: i18nUtils.get(`currency.label.0`) }];

    useEffect(() => {
        if (!flatPayments) {
            setPayments([]);
            return;
        }
        if (paymentsInfo?.length > 0) {
            const merged = flatPayments.map((payment) => {
                const prev = paymentsInfo.find((p) => p.key === payment.key);
                if (prev) {
                    return {
                        ...payment,
                        isChecked: prev.isChecked,
                        amount: prev.amount ?? payment.amount,
                        paymentReference: prev.paymentReference ?? payment.paymentReference,
                    };
                }
                return payment;
            });
            setPayments(merged);
            dispatch(servicePaymentsActions.updatePaymentsInfo(merged));
            merged.forEach((payment) => {
                const quantity = payment.amount?.quantity ?? payment.amount?.amount;
                const debtId = payment.concept?.debtId;
                const conceptCode = payment.concept?.conceptCode;
                let amountFieldName = null;
                if (debtId) {
                    amountFieldName = `${payment.key}_conceptAmount${debtId}`;
                } else if (conceptCode) {
                    amountFieldName = `${payment.key}_conceptAmount${conceptCode}`;
                }
                if (amountFieldName && quantity != null) {
                    setFieldValue(amountFieldName, { amount: quantity });
                }
                if (payment.paymentReference != null && payment.paymentReference !== "") {
                    setFieldValue(`${payment.key}_paymentReference`, payment.paymentReference);
                }
            });
        } else {
            setPayments(flatPayments);
        }
    }, [flatPayments]);

    useEffect(() => {
        let allPaymentsSelected =
            payments?.filter(({ isPending }) => !isPending).every(({ isChecked }) => isChecked) &&
            payments?.length !== 0;
        const atLeastOnePaymentSelected = payments?.some(({ isChecked }) => isChecked);

        if (payments?.every(({ isPending }) => isPending)) {
            allPaymentsSelected = false;
        }
        setAllSelected(allPaymentsSelected);
        setDisableButton(!atLeastOnePaymentSelected);
    }, [payments]);

    const setExpirationAmount = (payment) => {
        if (payment.concept?.date) {
            setFieldValue(`${payment.key}_conceptAmount${payment.concept?.debtId}`, {
                amount: payment.concept?.amount,
            });
        }
    };

    const handleSelectAll = (checked) => {
        const updatedPayments = payments.map(({ isPending, isChecked, ...payment }) => {
            setExpirationAmount(payment);
            return {
                ...payment,
                isChecked: isPending ? false : checked,
                isPending,
            };
        });
        setPayments(updatedPayments);
        dispatch(servicePaymentsActions.updatePaymentsInfo(updatedPayments));
    };

    const handleSelectOne = (payment) => {
        const { key: paymentKey, isPending } = payment;
        if (isPending) {
            return;
        }
        const updatedPayments = payments.map(({ isChecked, key, amount, concept, ...rest }) => ({
            ...rest,
            key,
            concept,
            isChecked: paymentKey === key ? !isChecked : isChecked,
            amount: paymentKey === key && concept?.date ? { quantity: concept?.amount } : amount,
        }));

        setExpirationAmount(payment);
        setPayments(updatedPayments);
        dispatch(servicePaymentsActions.updatePaymentsInfo(updatedPayments));
    };

    const handleUniquePayment = (payment) => {
        const { isChecked, key: paymentKey } = payment;
        if (isChecked) {
            const updatedPayments = payments.map(({ key, ...rest }) => ({
                ...rest,
                key,
                isChecked: key === paymentKey,
            }));
            setPayments(updatedPayments);
            dispatch(servicePaymentsActions.updatePaymentsInfo(updatedPayments));
            submitForm();
        } else {
            handleSelectOne(payment);
        }
    };

    const setQuantity = ({ key: paymentKey }, quantity) => {
        const updatedPayments = payments.map(({ amount, key, ...payment }) => ({
            ...payment,
            key,
            amount: paymentKey === key ? { quantity } : amount,
        }));
        setPayments(updatedPayments);
        dispatch(servicePaymentsActions.updatePaymentsInfo(updatedPayments));
    };

    const setPaymentReference = ({ key: paymentKey }, value) => {
        const updatedPayments = payments.map(({ paymentReference, key, ...payment }) => ({
            ...payment,
            key,
            paymentReference: paymentKey === key ? value : paymentReference,
        }));
        setPayments(updatedPayments);
        dispatch(servicePaymentsActions.updatePaymentsInfo(updatedPayments));
    };

    const renderNoPaymentsAvailable = () => (
        <div className={`text-center no-more-data ${!isDesktop} mx-4`} key="noMoreMovements">
            <div className="illustration-wrapper">
                <Image
                    src="images/coloredIcons/no-debts.svg"
                    className="svg-big-icon"
                    styles={{ width: isDesktop ? "96px" : "72px", height: "auto" }}
                />
            </div>
            <p
                className="text-lead"
                style={{
                    opacity: 1,
                    marginTop: isDesktop && "2rem",
                    fontFamily: !isDesktop && "Graphik-Light",
                    color: !isDesktop && "#707070",
                }}>
                <I18n id={`${FORM_ID}.empty.message`} />
            </p>
        </div>
    );

    const allowMultipleAFIP = () => {
        const showMultipleAFIPForCorporate = configUtils.getBoolean(
            "frontend.show.MultiplePaymentVEP.functionalities.Corporate",
            false,
        );
        const showMultipleAFIPForRetail = configUtils.getBoolean(
            "frontend.show.MultiplePaymentVEP.functionalities.Retail",
            false,
        );

        return isRetailEnvironment ? showMultipleAFIPForRetail : showMultipleAFIPForCorporate;
    };

    const renderList = () => (
        <>
            <Table>
                {payments.map((payment) => (
                    <ListItem
                        isDesktop={isDesktop}
                        isSmallDesktop={isSmallDesktop}
                        tableLabel={FORM_ID}
                        currencies={currencies}
                        payment={payment}
                        handleSelectOne={handleSelectOne}
                        handleUniquePayment={handleUniquePayment}
                        setQuantity={setQuantity}
                        setPaymentReference={setPaymentReference}
                        agendaPago={payment}
                    />
                ))}
            </Table>
            <div className="d-flex w-100 justify-content-end">
                <Pagination totalPages={totalPages} pageNumber={pageNumber} action={fetchNextPage} />
            </div>
        </>
    );

    const renderListMobile = () => {
        const endOfListItem = (
            <p className="text-center" key="noMoreTransactions">
                <I18n id="menu.payments.mobile.empty.message" />
            </p>
        );
        return (
            <InfiniteScroll
                dataLength={payments.length}
                next={fetchNextPage}
                hasMore={pageNumber < totalPages}
                loader={<Spinner />}
                endMessage={endOfListItem}
                scrollableTarget={isUxDashboardImprovementsEnabled() ? V3_SCROLL_ROOT_ID : undefined}>
                {payments.map((payment) => (
                    <ListItem
                        isDesktop={isDesktop}
                        isSmallDesktop={isSmallDesktop}
                        tableLabel={FORM_ID}
                        currencies={currencies}
                        payment={payment}
                        handleSelectOne={handleSelectOne}
                        handleUniquePayment={handleUniquePayment}
                        setQuantity={setQuantity}
                        setPaymentReference={setPaymentReference}
                        agendaPago={payment}
                    />
                ))}
            </InfiniteScroll>
        );
    };

    if (servicePaymentsSelected) {
        return (
            <Form autoComplete="off">
                <ListHeader
                    isDesktop={isDesktop}
                    FORM_ID={FORM_ID}
                    accounts={accounts}
                    submitForm={submitForm}
                    disableButton={disableButton}
                    payments={payments}
                    currencies={currencies}
                    setErrors={setErrors}
                    setTouched={setTouched}
                    defaultAccount={
                        typeof account === "object" && account?.idProduct
                            ? account.idProduct
                            : account ?? defaultAccount
                    }
                />
                <div
                    className={classNames(
                        "flex-grow scrollable",
                        !isDesktop && !payments && "no-results-container",
                        !isDesktop && "container-mobile-full-height",
                        payments !== undefined && !payments.length && "container-empty-results",
                    )}>
                    <section className="container--layout w-100">
                        <Grid rowSpacing={8} className="w-100 px-0">
                            <UiKitCol size={{ xs: 12 }} className="px-0">
                                <div
                                    className={classNames(
                                        "w-100",
                                        !isDesktop ? "d-flex bd-spacing-0-2" : "bd-spacing-0-8",
                                    )}>
                                    {payments && payments.length > 0 ? (
                                        <div className="w-100 my-1">
                                            <Checkbox
                                                className="mb-0 ml-3"
                                                labelText={`${FORM_ID}.selectAll`}
                                                name="selectAllPayments"
                                                checked={allSelected}
                                                onChange={handleSelectAll}
                                            />

                                            {isDesktop ? renderList() : renderListMobile()}
                                        </div>
                                    ) : (
                                        renderNoPaymentsAvailable()
                                    )}
                                </div>
                            </UiKitCol>
                        </Grid>
                    </section>
                </div>
            </Form>
        );
    }

    if (allowMultipleAFIP()) {
        return (
            <>
                <MultipleAFIPList isDesktop={isDesktop} isSmallDesktop={isSmallDesktop} />
            </>
        );
    }

    return (
        <>
            <PaymentAFIPList isDesktop={isDesktop} isSmallDesktop={isSmallDesktop} />
        </>
    );
};

const handlePrepareMultiplePayments = ({ account }, formikBag) => {
    const {
        props: { dispatch, paymentsInfo, account: accountObj },
        setTouched,
        setErrors,
    } = formikBag;
    const selectedPayments = paymentsInfo.filter(({ isChecked }) => isChecked);

    const total = selectedPayments.reduce((acc, val) => {
        const amount = val?.amount?.quantity || val.monto;
        return acc + amount;
    }, 0);

    if (total > accountObj?.balance) {
        setTouched({ account: true });
        setErrors({
            account: i18nUtils.get("services.multiplePayment.field.account.amountExceeded"),
        });
    } else {
        setErrors({});
        const preparePayments = buildPreparePaymentsRequest(selectedPayments);
        dispatch(servicePaymentsActions.cleanHandleBackFromTicket());
        dispatch(servicePaymentsActions.prepareMultiplePayments(preparePayments, account, formikBag));
    }
};

const mapStateToProps = (state) => ({
    servicePaymentsSelected: servicePaymentsSelectors.getServiceSelected(state),
    accounts: accountsSelectors.getAccounts(state),
    account: servicePaymentsSelectors.getMultiplePaymentsAccount(state),
    flatPayments: servicePaymentsSelectors.getFlatPayments(state),
    paymentsInfo: servicePaymentsSelectors.getMultiplePaymentsInfo(state),
    isRetailEnvironment: sessionSelectors.isRetailEnvironment(state),
    pageNumber: servicePaymentsSelectors.getPageNumber(state),
    totalPages: servicePaymentsSelectors.getTotalPages(state),
    defaultAccount: servicePaymentsSelectors.getDefaultAccount(state),
});

MultiplePaymentsList.defaultProps = {
    account: null,
    paymentsInfo: [],
};

MultiplePaymentsList.propTypes = {
    dispatch: func.isRequired,
    isDesktop: bool.isRequired,
    isSmallDesktop: bool.isRequired,
    servicePaymentsSelected: bool.isRequired,
    accounts: arrayOf(shape({})).isRequired,
    account: oneOfType([string, shape({})]),
    flatPayments: arrayOf(shape({})).isRequired,
    paymentsInfo: arrayOf(shape({})),
    submitForm: func.isRequired,
    isRetailEnvironment: bool.isRequired,
    setFieldValue: func.isRequired,
    setErrors: func.isRequired,
    setTouched: func.isRequired,
    totalPages: number.isRequired,
    pageNumber: number.isRequired,
    fetchNextPage: func.isRequired,
    defaultAccount: string.isRequired,
};

export default compose(
    connect(mapStateToProps),
    withFormik({
        validateOnChange: false,
        validateOnBlur: false,
        enableReinitialize: true,
        mapPropsToValues: (props) => {
            const accountId =
                typeof props.account === "object" && props.account?.idProduct
                    ? props.account.idProduct
                    : props.account ?? "";
            const initialValues = {
                account: accountId,
                ...props.componentValues,
            };

            if (props.paymentsInfo?.length) {
                props.paymentsInfo.forEach((payment) => {
                    const quantity = payment.amount?.quantity ?? payment.amount?.amount;
                    const debtId = payment.concept?.debtId;
                    const conceptCode = payment.concept?.conceptCode;
                    let amountFieldName = null;
                    if (debtId) {
                        amountFieldName = `${payment.key}_conceptAmount${debtId}`;
                    } else if (conceptCode) {
                        amountFieldName = `${payment.key}_conceptAmount${conceptCode}`;
                    }
                    if (amountFieldName && quantity != null) {
                        initialValues[amountFieldName] = { amount: quantity };
                    }
                    if (payment.paymentReference != null && payment.paymentReference !== "") {
                        initialValues[`${payment.key}_paymentReference`] = payment.paymentReference;
                    }
                });
            }

            return initialValues;
        },
        validationSchema: () =>
            Yup.lazy(() =>
                Yup.object().shape({
                    account: Yup.string().required(i18nUtils.get(`${FORM_ID}.field.error.required`)),
                }),
            ),
        handleSubmit: (values, formikBag) => handlePrepareMultiplePayments(values, formikBag),
    }),
)(MultiplePaymentsList);
